import {useCallback, useEffect, useRef, useState} from 'react';
import {getDisclosureCountsByParentIds, subscribeToAllReports} from '../../../firebase/firestore/reports';
import {getIcrMapByNumbers, resolveReportLinkedIcrNumber} from '../../../firebase/firestore/icr';
import {isIirProductType} from '../productTypes';
import {enrichDraftsWithIcr} from './useUserDrafts';

function resolveIcrNumber(report) {
    return resolveReportLinkedIcrNumber(report);
}

// FINTEL = approved, non-disclosure products — the only rows that can have child disclosures.
function isFintelReport(report) {
    const status = (report?.status || '').toLowerCase();
    const productType = report?.productType?.value;
    const isDisclosure = productType === 'disclosure' || productType === 'ndp1_disclosure';
    return status === 'approved' && !isDisclosure;
}

/**
 * Subscribe to all reports org-wide (no user filter).
 * Used by the unified Products listing page so every author appears in the filter.
 * Reports are enriched with `icrNumber` + `icrSubject` so the "Related ICR" column can render.
 */
export const useAllReports = () => {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [error, setError] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);
    const cancelRef = useRef(false);

    // Force a fresh re-fetch (e.g. after a row is deleted or reset to draft).
    const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

    useEffect(() => {
        setLoading(true);
        setError(null);
        cancelRef.current = false;

        const unsubscribe = subscribeToAllReports(
            async (data) => {
                if (cancelRef.current) return;
                // IIRs are raw collection data, not Intelligence Products — excluded from
                // the products page entirely (any status). They stay reachable via
                // /collection/iir and the ICR/EEI collection surfaces (P10 / MINSKY-16308).
                const rows = (Array.isArray(data) ? data : [])
                    .filter((r) => !isIirProductType(r?.productType));
                try {
                    const icrNumbers = rows.map(resolveIcrNumber).filter(Boolean);
                    const fintelIds = rows.filter(isFintelReport).map((r) => r.id);
                    const [icrByNumber, disclosureCounts] = await Promise.all([
                        icrNumbers.length ? getIcrMapByNumbers(icrNumbers) : Promise.resolve({}),
                        fintelIds.length ? getDisclosureCountsByParentIds(fintelIds) : Promise.resolve({}),
                    ]);
                    if (cancelRef.current) return;
                    const enriched = enrichDraftsWithIcr(rows, icrByNumber).map((r) => (
                        isFintelReport(r) ? {...r, disclosureCount: disclosureCounts[r.id] ?? 0} : r
                    ));
                    setReports(enriched);
                    setError(null);
                    setLoading(false);
                } catch (err) {
                    if (cancelRef.current) return;
                    // ICR enrichment is best-effort — fall back to raw rows so the list still renders.
                    console.error('Failed to enrich reports with ICR subjects', err);
                    setReports(rows);
                    setError(null);
                    setLoading(false);
                }
            },
            (err) => {
                if (cancelRef.current) return;
                console.error('Failed to subscribe to all reports', err);
                setError(err);
                setLoading(false);
            }
        );

        return () => {
            cancelRef.current = true;
            try {
                unsubscribe && unsubscribe();
            } catch (e) {
                // cleanup
            }
        };
    }, [reloadTick]);

    return {reports, loading, error, refresh};
};
